export interface BroadcastContactItem {
  id: number;
  name: string;
  number: string;
  avatarUrl?: string;
}

export function contactAddrKey(number: string): string {
  const raw = (number || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower.endsWith('@s.whatsapp.net')) {
    const user = lower.slice(0, lower.indexOf('@')).split(':')[0];
    const digits = user.replace(/\D/g, '');
    return digits || user;
  }
  if (lower.includes('@')) return lower;
  const digits = raw.replace(/\D/g, '');
  return digits || raw;
}

export function contactSelectionKey(c: BroadcastContactItem): string {
  if (c.id > 0) return `id:${c.id}`;
  return `addr:${contactAddrKey(c.number)}`;
}

/** Maps GET .../participants payload rows to broadcast picker contacts. */
export function mapRawParticipantToContactItem(p: Record<string, unknown> | null | undefined): BroadcastContactItem | null {
  if (!p || typeof p !== 'object') return null;

  const jid = String(p.jid ?? p.Jid ?? '').trim();
  let phone = String(p.phone_number ?? p.phoneNumber ?? p.number ?? p.Number ?? p.phone ?? p.Phone ?? '').trim();

  if (!phone && jid.includes('@s.whatsapp.net')) {
    phone = jid.split('@')[0].split(':')[0];
  } else if (!phone && jid.includes('@lid')) {
    phone = jid.split(':')[0];
  }

  const item: BroadcastContactItem = {
    id: Number(p.id ?? p.Id ?? 0) || 0,
    name: String(p.name ?? p.Name ?? phone ?? jid ?? 'Unknown'),
    number: phone,
    avatarUrl: (p.avatar_url ?? p.avatarUrl ?? p.AvatarUrl ?? undefined) as string | undefined,
  };

  if (!item.number?.trim()) return null;
  const n = item.number.trim();
  if (!n.includes('@')) {
    const digits = n.replace(/\D/g, '');
    if (digits.length < 8 || digits.length > 15) return null;
  }
  return item;
}

export interface GroupParticipantsApiResponse {
  success?: boolean;
  data?: unknown[];
  count?: number;
  message?: string | null;
  resolved_group_jid?: string | null;
}

/** Maps GET .../participants `data` array to broadcast picker contacts (skips invalid rows). */
export function dedupeBroadcastContactItems(items: BroadcastContactItem[]): BroadcastContactItem[] {
  const seen = new Set<string>();
  const out: BroadcastContactItem[] = [];
  for (const c of items) {
    const k = contactSelectionKey(c);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(c);
  }
  return out;
}

/** Maps GET .../participants `data` array to broadcast picker contacts (skips invalid rows). */
export function mapGroupParticipantsToContactItems(
  participants: unknown[] | null | undefined
): BroadcastContactItem[] {
  if (!participants?.length) return [];
  const contacts: BroadcastContactItem[] = [];
  for (const p of participants) {
    const it = mapRawParticipantToContactItem(p as Record<string, unknown>);
    if (it) contacts.push(it);
  }
  return dedupeBroadcastContactItems(contacts);
}

/** User-facing message when API returned no selectable contacts. */
export function resolveGroupParticipantsEmptyMessage(
  response: GroupParticipantsApiResponse | null | undefined,
  contactsLength: number
): string | null {
  if (contactsLength > 0) return null;
  const apiMsg = response?.message?.trim();
  if (apiMsg) return apiMsg;
  return 'Nenhum participante retornado para este grupo. Verifique a conexão do WhatsApp e tente de novo em alguns segundos.';
}

/** Never throws — safe error text for UI when axios/fetch fails. */
export function resolveGroupParticipantsErrorMessage(
  err: unknown,
  fallback = 'Erro ao carregar participantes do grupo.'
): string {
  if (!err || typeof err !== 'object') {
    return fallback;
  }
  const anyErr = err as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return (
    anyErr.response?.data?.message?.trim() ||
    anyErr.message?.trim() ||
    fallback
  );
}

/** whatsapp_groups.id for participants API (list `id` may be contacts.id). */
export function resolveWhatsAppGroupApiId(
  group: { id: number; whatsappGroupId?: number } | null | undefined,
  listId?: number | null
): number | null {
  if (group) {
    const waId = group.whatsappGroupId;
    if (waId != null && waId > 0) return waId;
    if (group.id > 0) return group.id;
    return null;
  }
  if (listId != null && listId > 0) return listId;
  return null;
}
